import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ContactForm, { ContactFormValues } from '../components/common/ContactForm';
import { useContacts } from '../hooks/useContacts';
import useInternalShipmentCountries from '../hooks/useInternalShipmentCountries';
import {
  useInternalShipment,
  useInternalShipmentMutations,
} from '../hooks/useInternalShipments';
import contactsService, { ContactFormData } from '../services/contactsService';
import { Page, PageHead, PageTitle, PageSub } from '../components/ui/Page';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import { Input, Select, Textarea } from '../components/ui/Input';
import { Callout } from '../components/ui/Callout';
import { Stepper } from '../components/ui/Stepper';
import { Divider } from '../components/ui/Divider';
import { Icon } from '../components/ui/Icon';
import { Dialog } from '../components/ui/radix/Dialog';

const STEPS = ['Sender', 'Recipient', 'Shipment details', 'Review'];

type ContactRole = 'sender' | 'recipient';

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || error?.message || fallback;

type Contact = {
  id: number | string;
  contactName: string;
  nickname?: string | null;
  companyName?: string | null;
  email?: string | null;
  phone?: string | null;
  cityName?: string | null;
  state_id?: number | null;
  state?: { name?: string | null } | null;
};

const ContactPreview: React.FC<{ title: string; contact: Contact | null }> = ({ title, contact }) => {
  if (!contact) return null;
  return (
    <Card pad style={{ background: 'var(--bg-tint)' }}>
      <div className="mono" style={{ fontSize: 11, color: 'var(--ink-4)', letterSpacing: '0.06em' }}>
        {title.toUpperCase()}
      </div>
      <div style={{ fontWeight: 600, marginTop: 6 }}>
        {contact.nickname || contact.contactName}
      </div>
      {contact.companyName && (
        <div className="muted" style={{ fontSize: 13 }}>{contact.companyName}</div>
      )}
      <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>
        {[contact.cityName, contact.state?.name].filter(Boolean).join(', ')}
      </div>
      {(contact.email || contact.phone) && (
        <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
          {contact.email && <div>{contact.email}</div>}
          {contact.phone && <div>{contact.phone}</div>}
        </div>
      )}
    </Card>
  );
};

const InternalShipmentCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const isEditMode = Boolean(editId);

  const [activeStep, setActiveStep] = useState(0);
  const [contactDialogRole, setContactDialogRole] = useState<ContactRole>('sender');
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [contactError, setContactError] = useState<string | null>(null);
  const [form, setForm] = useState({
    senderContactId: '',
    recipientContactId: '',
    itemDescription: '',
    weightKg: '',
    declaredValue: '',
    currency: 'ILS',
  });

  const {
    senderCountry,
    recipientCountry,
    isLoading: isLoadingCountries,
    error: countryLookupError,
  } = useInternalShipmentCountries();

  const senderContactsQuery = useContacts({
    page: 1,
    per_page: 500,
    country_id: senderCountry?.id,
    enabled: Boolean(senderCountry?.id),
  });
  const recipientContactsQuery = useContacts({
    page: 1,
    per_page: 500,
    country_id: recipientCountry?.id,
    enabled: Boolean(recipientCountry?.id),
  });
  const { shipment, isLoading: isLoadingShipment } = useInternalShipment(editId || undefined);
  const { createShipment, updateShipment, quoteShipment } = useInternalShipmentMutations();
  const quoteShipmentMutation = quoteShipment.mutate;
  const quoteShipmentAsync = quoteShipment.mutateAsync;

  const createContactMutation = useMutation({
    mutationFn: (payload: ContactFormData) => contactsService.addContact(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });

  useEffect(() => {
    if (shipment && isEditMode) {
      setForm({
        senderContactId: shipment.senderContactId ? String(shipment.senderContactId) : '',
        recipientContactId: shipment.recipientContactId ? String(shipment.recipientContactId) : '',
        itemDescription: shipment.itemDescription || '',
        weightKg: shipment.weightKg ? String(shipment.weightKg) : '',
        declaredValue:
          shipment.declaredValue !== null && shipment.declaredValue !== undefined
            ? String(shipment.declaredValue)
            : '',
        currency: shipment.currency || 'ILS',
      });
    }
  }, [shipment, isEditMode]);

  const selectedSender = useMemo<Contact | null>(
    () =>
      (senderContactsQuery.contacts.find((c) => String(c.id) === form.senderContactId) as Contact) ||
      null,
    [senderContactsQuery.contacts, form.senderContactId]
  );

  const selectedRecipient = useMemo<Contact | null>(
    () =>
      (recipientContactsQuery.contacts.find(
        (c) => String(c.id) === form.recipientContactId
      ) as Contact) || null,
    [recipientContactsQuery.contacts, form.recipientContactId]
  );

  useEffect(() => {
    const stateId = selectedRecipient?.state_id;
    const weight = Number(form.weightKg);
    if (stateId && weight > 0) {
      quoteShipmentMutation({ state_id: stateId, weightKg: weight, currency: form.currency });
    }
  }, [selectedRecipient?.state_id, form.weightKg, form.currency, quoteShipmentMutation]);

  const quote = quoteShipment.data?.data;
  const numericWeight = Number(form.weightKg);
  const hasValidShipmentDetails = Boolean(form.itemDescription.trim()) && numericWeight > 0;
  const hasRecipientState = Boolean(selectedRecipient?.state_id);
  const isQuoteCurrent = Boolean(
    quote &&
      selectedRecipient?.state_id &&
      quote.stateId === selectedRecipient.state_id &&
      Number(quote.weightKg) === numericWeight &&
      quote.currency === form.currency
  );

  const canContinue = () => {
    if (activeStep === 0) return Boolean(form.senderContactId);
    if (activeStep === 1) return Boolean(form.recipientContactId);
    if (activeStep === 2)
      return hasValidShipmentDetails && hasRecipientState && !quoteShipment.isPending;
    return true;
  };

  const handleContinue = async () => {
    if (activeStep < 2) {
      setActiveStep((c) => c + 1);
      return;
    }
    if (!hasValidShipmentDetails || !selectedRecipient?.state_id) return;
    try {
      if (!isQuoteCurrent) {
        await quoteShipmentAsync({
          state_id: selectedRecipient.state_id,
          weightKg: numericWeight,
          currency: form.currency,
        });
      }
      setActiveStep((c) => c + 1);
    } catch {
      setSubmitError(null);
    }
  };

  const handleOpenContactDialog = (role: ContactRole) => {
    setContactDialogRole(role);
    setContactError(null);
    setContactDialogOpen(true);
  };

  const handleCreateContact = async (values: ContactFormValues) => {
    setContactError(null);
    try {
      const payload: ContactFormData = {
        nickname: values.nickname,
        contactName: values.contactName,
        companyName: values.companyName || '',
        email: values.email,
        phone: values.phone,
        street: values.street,
        houseNo: values.houseNo || '',
        zipCode: values.zipCode || '',
        deliveryDetails: values.deliveryDetails || '',
        isFavorite: values.isFavorite || false,
        isDefault: values.isDefault || false,
        country_id: values.country_id || null,
        state_id: values.state_id || null,
        city_id: values.city_id || null,
        city_other: values.city_other || '',
      };
      const created = await createContactMutation.mutateAsync(payload);
      setForm((c) => ({
        ...c,
        [contactDialogRole === 'sender' ? 'senderContactId' : 'recipientContactId']: String(
          created.id
        ),
      }));
      setContactDialogOpen(false);
    } catch (err) {
      setContactError(getErrorMessage(err, 'Failed to create contact.'));
    }
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    try {
      const payload = {
        senderContactId: Number(form.senderContactId),
        recipientContactId: Number(form.recipientContactId),
        itemDescription: form.itemDescription.trim(),
        weightKg: Number(form.weightKg),
        declaredValue: form.declaredValue ? Number(form.declaredValue) : null,
        currency: form.currency,
      };
      const response =
        isEditMode && editId
          ? await updateShipment.mutateAsync({ id: editId, payload })
          : await createShipment.mutateAsync(payload);
      navigate(`/internal-shipments/${response.data.id}`);
    } catch (err) {
      setSubmitError(
        getErrorMessage(err, `Failed to ${isEditMode ? 'update' : 'create'} internal shipment.`)
      );
    }
  };

  const isLoadingPage =
    isLoadingCountries ||
    senderContactsQuery.isLoading ||
    recipientContactsQuery.isLoading ||
    (isEditMode && isLoadingShipment);

  if (isLoadingPage) {
    return (
      <Page width="wide">
        <div className="muted" style={{ textAlign: 'center', padding: 64 }}>
          Loading…
        </div>
      </Page>
    );
  }

  const quoteError = quoteShipment.error
    ? getErrorMessage(quoteShipment.error, 'Rate not configured for this state.')
    : null;
  const dialogCountry = contactDialogRole === 'sender' ? senderCountry : recipientCountry;

  const stepperSteps = STEPS.map((label, i) => ({
    label,
    status: (i === activeStep ? 'active' : i < activeStep ? 'done' : 'pending') as
      | 'active'
      | 'done'
      | 'pending',
  }));

  return (
    <Page width="wide">
      <Button
        variant="ghost"
        size="sm"
        onClick={() =>
          navigate(isEditMode && editId ? `/internal-shipments/${editId}` : '/internal-shipments')
        }
        style={{ marginBottom: 16 }}
      >
        <Icon name="arrowLeft" size={14} />
        <span>{isEditMode ? 'Back to shipment' : 'Back to shipments'}</span>
      </Button>

      <PageHead>
        <div>
          <PageTitle>{isEditMode ? 'Edit internal shipment' : 'New internal shipment'}</PageTitle>
          <PageSub>
            {isEditMode
              ? 'Update a pending internal shipment.'
              : 'Create an internal shipment from Israel to the United States using your saved contacts.'}
          </PageSub>
        </div>
      </PageHead>

      {countryLookupError && (
        <div style={{ marginBottom: 16 }}>
          <Callout tone="warn">
            <Icon name="alertCircle" size={16} />
            <span>
              Failed to load the required Israel and United States country settings for internal
              shipments.
            </span>
          </Callout>
        </div>
      )}

      <Card pad style={{ padding: 28 }}>
        <Stepper steps={stepperSteps} />

        {submitError && (
          <div style={{ marginTop: 16 }}>
            <Callout tone="warn">
              <Icon name="alertCircle" size={16} />
              <span>{submitError}</span>
            </Callout>
          </div>
        )}

        <div style={{ marginTop: 24 }}>
          {activeStep === 0 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12 }}>
                <div>
                  <h3 style={{ fontSize: 16 }}>Sender (Israel only)</h3>
                  <p className="muted" style={{ marginTop: 4 }}>
                    Internal shipments must start from an Israel sender contact.
                  </p>
                </div>
                <Button onClick={() => handleOpenContactDialog('sender')}>
                  <Icon name="plus" size={14} />
                  <span>Add Israel contact</span>
                </Button>
              </div>

              {senderContactsQuery.contacts.length === 0 && (
                <div style={{ marginBottom: 16 }}>
                  <Callout tone="info">
                    <Icon name="info" size={16} />
                    <span>No Israel sender contacts are available yet. Create one to continue.</span>
                  </Callout>
                </div>
              )}

              <Field label="Sender contact">
                <Select
                  value={form.senderContactId}
                  onChange={(e) => setForm((c) => ({ ...c, senderContactId: e.target.value }))}
                >
                  <option value="">Select a sender…</option>
                  {senderContactsQuery.contacts.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.nickname || c.contactName} — {c.contactName}
                    </option>
                  ))}
                </Select>
              </Field>

              {selectedSender && (
                <div style={{ marginTop: 16 }}>
                  <ContactPreview title="Sender preview" contact={selectedSender} />
                </div>
              )}
            </div>
          )}

          {activeStep === 1 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12 }}>
                <div>
                  <h3 style={{ fontSize: 16 }}>Recipient (United States only)</h3>
                  <p className="muted" style={{ marginTop: 4 }}>
                    Recipient contacts must be in the United States and include a US state.
                  </p>
                </div>
                <Button onClick={() => handleOpenContactDialog('recipient')}>
                  <Icon name="plus" size={14} />
                  <span>Add US contact</span>
                </Button>
              </div>

              {recipientContactsQuery.contacts.length === 0 && (
                <div style={{ marginBottom: 16 }}>
                  <Callout tone="info">
                    <Icon name="info" size={16} />
                    <span>
                      No United States recipient contacts are available yet. Create one to continue.
                    </span>
                  </Callout>
                </div>
              )}

              <Field label="Recipient contact">
                <Select
                  value={form.recipientContactId}
                  onChange={(e) =>
                    setForm((c) => ({ ...c, recipientContactId: e.target.value }))
                  }
                >
                  <option value="">Select a recipient…</option>
                  {recipientContactsQuery.contacts.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.nickname || c.contactName} — {c.contactName}
                    </option>
                  ))}
                </Select>
              </Field>

              {selectedRecipient && !selectedRecipient.state_id && (
                <div style={{ marginTop: 16 }}>
                  <Callout tone="warn">
                    <Icon name="alertCircle" size={16} />
                    <span>
                      The selected recipient must include a US state before this shipment can be
                      quoted.
                    </span>
                  </Callout>
                </div>
              )}

              {selectedRecipient && (
                <div style={{ marginTop: 16 }}>
                  <ContactPreview title="Recipient preview" contact={selectedRecipient} />
                </div>
              )}
            </div>
          )}

          {activeStep === 2 && (
            <div>
              <h3 style={{ fontSize: 16, marginBottom: 16 }}>Shipment details</h3>
              <Field label="Item description">
                <Textarea
                  rows={4}
                  value={form.itemDescription}
                  onChange={(e) =>
                    setForm((c) => ({ ...c, itemDescription: e.target.value }))
                  }
                />
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 16 }}>
                <Field label="Weight (kg)">
                  <Input
                    type="number"
                    value={form.weightKg}
                    min={0}
                    step={0.001}
                    onChange={(e) => setForm((c) => ({ ...c, weightKg: e.target.value }))}
                  />
                </Field>
                <Field label="Declared value">
                  <Input
                    type="number"
                    value={form.declaredValue}
                    min={0}
                    step={0.01}
                    onChange={(e) => setForm((c) => ({ ...c, declaredValue: e.target.value }))}
                  />
                </Field>
                <Field label="Currency">
                  <Input
                    value={form.currency}
                    onChange={(e) =>
                      setForm((c) => ({ ...c, currency: e.target.value.toUpperCase() }))
                    }
                  />
                </Field>
              </div>

              <Divider style={{ margin: '24px 0' }} />

              {!selectedRecipient?.state_id && (
                <div style={{ marginBottom: 12 }}>
                  <Callout tone="warn">
                    <Icon name="alertCircle" size={16} />
                    <span>
                      The selected recipient needs a United States state before a quote can be
                      calculated.
                    </span>
                  </Callout>
                </div>
              )}
              {quoteShipment.isPending && (
                <div className="muted" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Icon name="refresh" size={14} /> Calculating quote…
                </div>
              )}
              {quoteError && (
                <div style={{ marginTop: 12 }}>
                  <Callout tone="warn">
                    <Icon name="alertCircle" size={16} />
                    <span>{quoteError}</span>
                  </Callout>
                </div>
              )}
              {quote && (
                <div style={{ marginTop: 12 }}>
                  <Callout tone="ok">
                    <Icon name="checkCircle" size={16} />
                    <span>
                      Quoted cost: <strong>{quote.currency} {quote.quotedCost}</strong> for tier{' '}
                      {quote.tier.label}
                    </span>
                  </Callout>
                </div>
              )}
            </div>
          )}

          {activeStep === 3 && (
            <div>
              <Callout tone="info">
                <Icon name="info" size={16} />
                <span>
                  Review the Israel sender, US recipient, and quoted cost before submitting.
                </span>
              </Callout>
              <div className="grid-2" style={{ marginTop: 16 }}>
                <ContactPreview title="Sender" contact={selectedSender} />
                <ContactPreview title="Recipient" contact={selectedRecipient} />
              </div>
              <Card pad style={{ marginTop: 16 }}>
                <h3 style={{ fontSize: 16, marginBottom: 12 }}>Shipment summary</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14 }}>
                  <div>
                    <strong>Item:</strong> {form.itemDescription || 'N/A'}
                  </div>
                  <div>
                    <strong>Weight:</strong> {form.weightKg || '0'} kg
                  </div>
                  <div>
                    <strong>Declared value:</strong> {form.declaredValue || '0'}
                  </div>
                  <div>
                    <strong>Quoted cost:</strong>{' '}
                    {quote ? `${quote.currency} ${quote.quotedCost}` : 'Unavailable'}
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32 }}>
          <Button
            variant="ghost"
            disabled={activeStep === 0}
            onClick={() => setActiveStep((c) => c - 1)}
          >
            <Icon name="arrowLeft" size={14} />
            <span>Back</span>
          </Button>
          <div style={{ display: 'flex', gap: 8 }}>
            {activeStep < STEPS.length - 1 && (
              <Button variant="blue" disabled={!canContinue()} onClick={handleContinue}>
                <span>{activeStep === 2 && !isQuoteCurrent ? 'Review quote' : 'Continue'}</span>
                <Icon name="arrowRight" size={14} />
              </Button>
            )}
            {activeStep === STEPS.length - 1 && (
              <Button
                variant="blue"
                disabled={createShipment.isPending || updateShipment.isPending}
                onClick={handleSubmit}
              >
                <Icon name="check" size={14} />
                <span>{isEditMode ? 'Save changes' : 'Create shipment'}</span>
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Dialog
        open={contactDialogOpen}
        onOpenChange={setContactDialogOpen}
        title={`Create ${contactDialogRole === 'sender' ? 'sender' : 'recipient'} contact`}
        maxWidth={760}
      >
        <ContactForm
          key={`${contactDialogRole}-${dialogCountry?.id ?? 'country'}`}
          onSubmit={handleCreateContact}
          isSubmitting={createContactMutation.isPending}
          formError={contactError}
          lockedCountryId={dialogCountry?.id}
          lockedCountryName={dialogCountry?.name}
          lockedCountryIso2={dialogCountry?.iso2}
        />
      </Dialog>
    </Page>
  );
};

export default InternalShipmentCreatePage;
